import { useState } from 'react';
import '../../pages/partnership-font.css';
import { ShopBannerStyle } from '../../lib/api/shopBanners';
import styles from './ShopBannerPreview.module.css';

/** Countdown texts per shop language, as in the theme (snippets/campaign-banners.liquid). */
const TIMER_TEXT: Record<string, [string, string, string, string, string]> = {
  en: ['Offer ends in', 'Day(s)', 'Hour(s)', 'Min(s)', 'Sec(s)'],
  nl: ['Actie eindigt over', 'Dag(en)', 'Uur', 'Min.', 'Sec.'],
  fr: ["L'offre se termine dans", 'Jour(s)', 'Heure(s)', 'Min.', 'Sec.'],
  da: ['Tilbuddet slutter om', 'Dag(e)', 'Time(r)', 'Min.', 'Sek.'],
  de: ['Angebot endet in', 'Tag(e)', 'Stunde(n)', 'Min.', 'Sek.'],
  es: ['La oferta termina en', 'Día(s)', 'Hora(s)', 'Min.', 'Seg.'],
  it: ["L'offerta termina tra", 'Giorno/i', 'Ore', 'Min.', 'Sec.'],
  pl: ['Oferta kończy się za', 'Dni', 'Godz.', 'Min.', 'Sek.'],
  'pt-PT': ['A oferta termina em', 'Dia(s)', 'Hora(s)', 'Min.', 'Seg.'],
};

interface Props {
  style: ShopBannerStyle;
  eyebrow: string;
  headline: string;
  subline: string;
  smallPrint: string;
  /** Show the countdown (the shop only does in the last hours, or during a recurring window). */
  showCountdown: boolean;
  /** Example time left in seconds; under a day the shop hides the days tile. */
  secondsLeft?: number;
  locale?: string;
  /** Preview width in px — the shop's product column is ~600px on desktop. */
  width?: number;
}

/** The product-page banner as the shop renders it. */
export const ShopBannerPreview = ({
  style, eyebrow, headline, subline, smallPrint, showCountdown,
  secondsLeft = 31 * 3600 + 42 * 60 + 18, locale = 'en', width = 600,
}: Props) => {
  const [label, ...units] = TIMER_TEXT[locale] ?? TIMER_TEXT.en;
  const left = Math.max(0, Math.floor(secondsLeft));
  const tiles = [Math.floor(left / 86400), Math.floor(left / 3600) % 24, Math.floor(left / 60) % 60, left % 60]
    .map((n, i) => [String(n).padStart(2, '0'), units[i]])
    .filter((_, i) => i > 0 || left >= 86400);
  // Same rule as the theme: conditions longer than about one line fold away behind an (i)
  const foldTerms = smallPrint.length > 100;
  const [termsOpen, setTermsOpen] = useState(false);
  return (
    <div className={styles.wrap} style={{ width, maxWidth: '100%' }}>
      <div className={`${styles.card} ${style !== 'yellow' ? styles[style] : ''}`}>
        <div className={styles.text}>
          {eyebrow.trim() && <p className={styles.eyebrow}>{eyebrow}</p>}
          <p className={`${styles.headline} ${headline.trim() ? '' : styles.placeholder}`}>
            {headline.trim() || 'Your headline'}
          </p>
          {subline.trim() && <p className={styles.subline}>{subline}</p>}
          {foldTerms && (
            <button
              type="button"
              className={styles.info}
              aria-expanded={termsOpen}
              aria-label="Offer conditions"
              title="Offer conditions"
              onClick={() => setTermsOpen((open) => !open)}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.6" />
                <path d="M10 9v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                <circle cx="10" cy="6.25" r="1.05" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>
        {showCountdown && (
          <div className={styles.timer}>
            <p className={styles.timerLabel}>{label}</p>
            <div className={styles.clock}>
              {tiles.map(([n, u], i) => (
                <span key={u} className={styles.unit}>
                  <span className={styles.cell}>
                    <span className={styles.num}>{n}</span>
                    <span className={styles.unitLabel}>{u}</span>
                  </span>
                  {i < tiles.length - 1 && <span className={styles.sep}>:</span>}
                </span>
              ))}
            </div>
          </div>
        )}
        {smallPrint.trim() && (!foldTerms || termsOpen) && (
          <p className={`${styles.smallPrint} ${showCountdown ? styles.afterTimer : ''}`}>{smallPrint}</p>
        )}
      </div>
    </div>
  );
};

export default ShopBannerPreview;
